import { useState } from 'react'

import { Botao, Vazio } from '@/componentes/ui'
import { usePerfil } from '@/lib/sessao'
import { cn } from '@/lib/utilitarios'
import { SecaoAcompanhamento } from './SecaoAcompanhamento'
import { SecaoCardapios } from './SecaoCardapios'
import { SecaoDias } from './SecaoDias'
import { SecaoLembretes } from './SecaoLembretes'
import { SecaoNotificacoes } from './SecaoNotificacoes'
import { SecaoParticipantes } from './SecaoParticipantes'
import { SecaoTurmas } from './SecaoTurmas'
import { SecaoVideos } from './SecaoVideos'

/**
 * Área administrativa (seção 14).
 *
 * Vive dentro do mesmo app, em /admin, protegida por `perfis.papel`.
 * Não é um segundo projeto: para uma turma de 6 pessoas, um deploy a
 * mais custaria mais do que entrega.
 *
 * A proteção de verdade não está aqui — está no RLS. Esta tela só
 * evita mostrar controles inúteis a quem não é administradora; mesmo
 * que alguém force a rota, o banco recusa toda escrita.
 */

type Aba =
  | 'acompanhamento'
  | 'participantes'
  | 'turmas'
  | 'dias'
  | 'videos'
  | 'cardapios'
  | 'lembretes'
  | 'avisos'

const ABAS: { id: Aba; rotulo: string; emoji: string }[] = [
  { id: 'acompanhamento', rotulo: 'Acompanhamento', emoji: '📊' },
  { id: 'participantes', rotulo: 'Participantes', emoji: '👥' },
  { id: 'turmas', rotulo: 'Turmas', emoji: '🎓' },
  { id: 'dias', rotulo: 'Os 28 dias', emoji: '📅' },
  { id: 'videos', rotulo: 'Vídeos', emoji: '🎬' },
  { id: 'cardapios', rotulo: 'Cardápios', emoji: '🍽️' },
  { id: 'lembretes', rotulo: 'Lembretes', emoji: '🔔' },
  { id: 'avisos', rotulo: 'Avisos', emoji: '📣' },
]

export function PaginaAdmin({ aoSair }: { aoSair: () => void }) {
  const { data: perfil, isLoading } = usePerfil()
  const [aba, setAba] = useState<Aba>('acompanhamento')

  if (isLoading) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-10">
        <p className="text-sm text-[var(--tinta-apagada)]">Carregando…</p>
      </main>
    )
  }

  if (perfil?.papel !== 'admin') {
    return (
      <main className="mx-auto max-w-md px-5 py-10">
        <Vazio
          emoji="🔒"
          titulo="Área restrita"
          descricao="Esta área é da administração do programa."
          acao={
            <Botao variante="contorno" tamanho="pequeno" onClick={aoSair}>
              Voltar para o app
            </Botao>
          }
        />
      </main>
    )
  }

  return (
    <div className="mx-auto max-w-3xl px-5 pt-6 pb-16">
      <header className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-titulo text-2xl font-bold">Administração</h1>
          <p className="mt-0.5 text-sm text-[var(--tinta-suave)]">Projeto Viver</p>
        </div>
        <Botao variante="fantasma" tamanho="pequeno" onClick={aoSair}>
          Ver o app
        </Botao>
      </header>

      <nav className="-mx-5 mb-6 overflow-x-auto px-5" aria-label="Seções da administração">
        <div className="flex w-max gap-2">
          {ABAS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setAba(item.id)}
              aria-current={aba === item.id ? 'true' : undefined}
              className={cn(
                'flex h-11 items-center gap-1.5 rounded-full px-4 font-titulo text-sm font-semibold whitespace-nowrap transition-colors',
                aba === item.id
                  ? 'bg-coral text-white'
                  : 'bg-[var(--cartao)] text-[var(--tinta-suave)]',
              )}
            >
              <span aria-hidden>{item.emoji}</span>
              {item.rotulo}
            </button>
          ))}
        </div>
      </nav>

      {aba === 'acompanhamento' && <SecaoAcompanhamento />}
      {aba === 'participantes' && <SecaoParticipantes />}
      {aba === 'turmas' && <SecaoTurmas />}
      {aba === 'dias' && <SecaoDias />}
      {aba === 'videos' && <SecaoVideos />}
      {aba === 'cardapios' && <SecaoCardapios />}
      {aba === 'lembretes' && <SecaoLembretes />}
      {aba === 'avisos' && <SecaoNotificacoes />}
    </div>
  )
}
